import { createContext, useContext, useId, type ReactNode } from "react";
import { cx } from "../util";

interface FieldCtx {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldCtx | null>(null);

/** Inside a <Field>, controls pick up id / aria-describedby / aria-invalid automatically. */
export function useFieldControl(props: {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
  invalid?: boolean;
  required?: boolean;
}) {
  const ctx = useContext(FieldContext);
  const invalid = props.invalid ?? (props["aria-invalid"] === true || props["aria-invalid"] === "true");
  return {
    id: props.id ?? ctx?.id,
    "aria-describedby": [props["aria-describedby"], ctx?.describedBy].filter(Boolean).join(" ") || undefined,
    "aria-invalid": invalid || ctx?.invalid ? true : undefined,
    required: props.required ?? ctx?.required ?? undefined,
    invalid: invalid || !!ctx?.invalid,
  };
}

export interface FieldProps {
  label?: ReactNode;
  /** Helper text under the control. */
  hint?: ReactNode;
  /** Error text; replaces the hint and marks the control invalid. */
  error?: ReactNode;
  required?: boolean;
  /** Something right-aligned on the label row (e.g. a "Forgot password?" link). */
  labelAction?: ReactNode;
  /** Use your own id for the control. */
  id?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required = false, labelAction, id, className, children }: FieldProps) {
  const auto = useId();
  const controlId = id ?? `gf-f${auto}`;
  const hintId = `${controlId}-hint`;
  const errId = `${controlId}-err`;
  const hasError = error !== undefined && error !== null && error !== false && error !== "";
  const hasHint = hint !== undefined && hint !== null && hint !== false && hint !== "";
  const ctx: FieldCtx = {
    id: controlId,
    describedBy: hasError ? errId : hasHint ? hintId : undefined,
    invalid: hasError,
    required,
  };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cx("gf-field", hasError && "gf-field--invalid", className)}>
        {label || labelAction ? (
          <div className="gf-field__top">
            {label ? (
              <label className="gf-field__label" htmlFor={controlId}>
                {label}
                {required ? <span className="gf-field__req" aria-hidden="true">*</span> : null}
              </label>
            ) : (
              <span />
            )}
            {labelAction ? <span className="gf-field__action">{labelAction}</span> : null}
          </div>
        ) : null}
        {children}
        {hasError ? (
          <p className="gf-field__error" id={errId} role="alert">
            {error}
          </p>
        ) : hasHint ? (
          <p className="gf-field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
